import { useEffect, useRef, useState } from 'react';
import {
  Play,
  Pause,
  ClockCounterClockwise,
  ClockClockwise,
  SpeakerHigh,
  SpeakerSlash,
  SkipForward,
  X,
  Plus,
  Minus,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAudioPlayerControls, useAudioPlayerState } from 'shared/hooks/useAudioPlayer';
import { formatTime, formatRuntime } from 'shared/utils/formatTime';
import { cn } from 'shared/utils/cn';
import type { PublishedEpisode } from 'shared/data/episodes';

export type PlayerShellVariant = 'inline' | 'compact' | 'full';

interface PlayerShellProps {
  variant: PlayerShellVariant;
  episode: PublishedEpisode;
}

const MIN_RATE = 0.5;
const MAX_RATE = 2;
const RATE_STEP = 0.05;
const PRESET_RATES = [0.75, 1, 1.25, 1.5, 2];

export function PlayerShell({ variant, episode }: PlayerShellProps) {
  const { t } = useTranslation();
  const {
    currentEpisode,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackRate,
    hasOlderEpisode,
  } = useAudioPlayerState();
  const {
    play,
    pause,
    seek,
    skipBackward,
    skipForward,
    setVolume,
    toggleMute,
    close,
    setPlaybackRate,
    playOlderEpisode,
  } = useAudioPlayerControls();

  const isActiveEpisode = currentEpisode?.buzzsproutId === episode.buzzsproutId;
  const effectiveTime = isActiveEpisode ? currentTime : 0;
  const effectiveDuration =
    isActiveEpisode && duration > 0 ? duration : episode.durationSeconds;
  const effectivePlaying = isActiveEpisode && isPlaying;
  const scrubberDisabled = !isActiveEpisode || effectiveDuration <= 0;
  const skipDisabled = !isActiveEpisode;

  const showInfo = variant === 'full';
  const showClose = variant !== 'inline';

  const onPlayPause = () => {
    if (effectivePlaying) pause();
    else play(episode);
  };

  const onSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isActiveEpisode) return;
    seek(Number(e.target.value));
  };

  const coverImage = episode.image.src;
  const coverAlt = episode.image.alt ?? '';
  const currentRuntime = formatRuntime(effectiveTime);
  const totalRuntime = formatRuntime(effectiveDuration);

  return (
    <div
      className={cn(
        'bg-primary-container/80 backdrop-blur-xl border border-outline-variant/5 shadow-[0_-10px_50px_rgba(0,0,0,0.5)]',
        'flex items-center sm:gap-3 lg:gap-4',
        'transition-[gap,border-radius,padding] duration-300 ease-out',
        variant === 'full' && 'gap-3 rounded-3xl sm:rounded-full p-2 sm:p-3',
        variant === 'compact' && 'gap-2 rounded-2xl sm:rounded-full p-2 md:p-3',
        variant === 'inline' && 'gap-2 rounded-full p-2 md:p-3',
      )}
      aria-label={t('audioPlayer.nowPlaying')}
    >
      {variant !== 'inline' && (
        <div
          aria-hidden={variant !== 'full'}
          className={cn(
            'sm:hidden flex-shrink-0 overflow-hidden',
            'transition-[max-width,opacity] duration-300 ease-out',
            variant === 'full' ? 'max-w-[2rem] opacity-100' : 'max-w-0 opacity-0',
          )}
        >
          <Link
            to={`/episodes/${episode.number}`}
            tabIndex={variant === 'full' ? 0 : -1}
            aria-label={t('audioPlayer.openEpisode', { title: episode.title })}
            className={cn(
              'block rounded-full',
              'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
            )}
          >
            <img
              src={coverImage}
              alt={coverAlt}
              className="w-8 h-8 rounded-full object-cover shadow-lg"
            />
          </Link>
        </div>
      )}
      {variant !== 'inline' && (
        <div className="hidden sm:block">
          <InfoColumn
            episode={episode}
            coverImage={coverImage}
            coverAlt={coverAlt}
            currentRuntime={currentRuntime}
            totalRuntime={totalRuntime}
            expanded={showInfo}
          />
        </div>
      )}

      <div
        className={cn(
          'flex-1 min-w-0 flex flex-col items-center sm:gap-2',
          variant === 'full' &&
            'sm:justify-center sm:min-h-[4.5rem] md:justify-start md:min-h-0',
        )}
      >
        {variant !== 'inline' && (
          <div
            aria-hidden={variant !== 'full'}
            className={cn(
              'w-full overflow-hidden sm:hidden',
              'transition-[max-height,opacity] duration-300 ease-out',
              variant === 'full' ? 'max-h-[2rem] opacity-100' : 'max-h-0 opacity-0',
            )}
          >
            <Link
              to={`/episodes/${episode.number}`}
              tabIndex={variant === 'full' ? 0 : -1}
              aria-label={t('audioPlayer.openEpisode', { title: episode.title })}
              className={cn(
                'w-full min-w-0 flex items-center gap-2 rounded-sm pr-2',
                'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
              )}
            >
              <span className="flex-1 min-w-0 font-serif text-sm text-on-surface truncate">
                {episode.title}
              </span>
              <span className="flex-shrink-0 font-sans text-[11px] tabular-nums opacity-80">
                {currentRuntime}
              </span>
            </Link>
          </div>
        )}
        <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
          <SpeedMenu rate={playbackRate} onChange={setPlaybackRate} />

          <button
            type="button"
            onClick={() => skipBackward()}
            disabled={skipDisabled}
            aria-label={t('audioPlayer.rewind')}
            className={controlIconClasses()}
          >
            <ClockCounterClockwise size={22} />
          </button>

          <button
            type="button"
            onClick={onPlayPause}
            aria-label={effectivePlaying ? t('audioPlayer.pause') : t('audioPlayer.play')}
            className={cn(
              'group min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full',
              'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
            )}
          >
            <span
              className={cn(
                variant === 'full' && 'w-9 h-9 sm:w-11 sm:h-11 md:w-12 md:h-12',
                variant === 'compact' && 'w-8 h-8 sm:w-11 sm:h-11 md:w-12 md:h-12',
                variant === 'inline' && 'w-11 h-11 md:w-12 md:h-12',
                'bg-tertiary text-on-tertiary rounded-full flex items-center justify-center',
                'group-hover:scale-105 group-active:scale-95 transition-[width,height,transform] duration-300 ease-out shadow-[0_0_20px_rgba(234,195,73,0.3)]',
              )}
            >
              {effectivePlaying ? (
                <Pause size={24} weight="fill" />
              ) : (
                <Play size={24} weight="fill" />
              )}
            </span>
          </button>

          <button
            type="button"
            onClick={() => skipForward()}
            disabled={skipDisabled}
            aria-label={t('audioPlayer.fastForward')}
            className={controlIconClasses()}
          >
            <ClockClockwise size={22} />
          </button>

          {variant !== 'inline' ? (
            <button
              type="button"
              onClick={playOlderEpisode}
              disabled={!hasOlderEpisode}
              aria-label={t('audioPlayer.playOlderEpisode')}
              className={controlIconClasses()}
            >
              <SkipForward size={22} weight="fill" />
            </button>
          ) : (
            <div aria-hidden="true" className="min-w-[44px] min-h-[44px]" />
          )}
        </div>

        <div
          className={cn(
            'items-center gap-2 lg:gap-3 w-full min-w-0',
            variant === 'inline' && 'hidden sm:flex px-8 lg:px-12',
            variant === 'compact' && 'hidden sm:flex pl-8 lg:pl-12',
            variant === 'full' && 'hidden md:flex',
          )}
        >
          <span className="text-xs font-sans opacity-80 tabular-nums">
            {formatTime(effectiveTime)}
          </span>
          <input
            type="range"
            min={0}
            max={effectiveDuration || 0}
            step={0.1}
            value={effectiveTime}
            onChange={onSeek}
            disabled={scrubberDisabled}
            aria-label={t('audioPlayer.seekLabel')}
            aria-valuetext={`${formatTime(effectiveTime)} of ${formatTime(effectiveDuration)}`}
            className="flex-1 h-1 bg-outline-variant rounded-full cursor-pointer appearance-none accent-tertiary disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="text-xs font-sans opacity-80 tabular-nums">
            {formatTime(effectiveDuration)}
          </span>
        </div>
      </div>

      {variant === 'inline' && (
        <span className="sm:hidden flex-shrink-0 pr-2 font-sans text-sm font-bold tabular-nums opacity-80">
          {currentRuntime}
        </span>
      )}

      <div className="hidden sm:flex">
        <VolumeControl
          volume={volume}
          isMuted={isMuted}
          onToggleMute={toggleMute}
          onVolumeChange={setVolume}
        />
      </div>

      {showClose && (
        <button
          type="button"
          onClick={close}
          aria-label={t('audioPlayer.close')}
          className={cn(
            'flex min-w-[44px] min-h-[44px] items-center justify-center rounded-full flex-shrink-0',
            'text-on-surface-variant hover:text-tertiary transition-colors',
            'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
          )}
        >
          <X size={20} weight="bold" />
        </button>
      )}
    </div>
  );
}

function controlIconClasses() {
  return cn(
    'min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full',
    'text-on-surface-variant hover:text-tertiary transition-colors',
    'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
    'disabled:opacity-[0.38] disabled:pointer-events-none',
  );
}

interface InfoColumnProps {
  episode: PublishedEpisode;
  coverImage: string;
  coverAlt: string;
  currentRuntime: string;
  totalRuntime: string;
  expanded: boolean;
}

function InfoColumn({
  episode,
  coverImage,
  coverAlt,
  currentRuntime,
  totalRuntime,
  expanded,
}: InfoColumnProps) {
  const { t } = useTranslation();
  return (
    <div
      aria-hidden={!expanded}
      className={cn(
        'overflow-hidden transition-[max-width,opacity] duration-300 ease-out',
        expanded
          ? 'max-w-[180px] sm:max-w-[220px] lg:max-w-[280px] opacity-100'
          : 'max-w-0 opacity-0',
      )}
    >
      <Link
        to={`/episodes/${episode.number}`}
        tabIndex={expanded ? 0 : -1}
        aria-label={t('audioPlayer.openEpisode', { title: episode.title })}
        className={cn(
          'flex items-center gap-3 pl-1 pr-2',
          'rounded-full focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
        )}
      >
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden flex-shrink-0 shadow-lg">
          <img
            src={coverImage}
            alt={coverAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="hidden sm:inline font-serif text-on-surface text-sm md:text-base truncate max-w-[180px] lg:max-w-[240px]">
            {episode.title}
          </span>
          <span className="font-sans text-[11px] text-tertiary font-bold opacity-80 truncate tabular-nums md:hidden">
            <span className="sm:hidden inline-block min-w-[4.5rem]">{currentRuntime}</span>
            <span className="hidden sm:inline-block min-w-[8.5rem]">
              {currentRuntime}/{totalRuntime}
            </span>
          </span>
        </div>
      </Link>
    </div>
  );
}

interface SpeedMenuProps {
  rate: number;
  onChange: (rate: number) => void;
}

function SpeedMenu({ rate, onChange }: SpeedMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useOutsideInteraction(rootRef, () => setOpen(false));

  useEffect(() => {
    if (!open) return;
    const slider = dialogRef.current?.querySelector<HTMLElement>('input[type="range"]');
    slider?.focus();
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  const adjust = (delta: number) => {
    const next = Math.round((rate + delta) * 100) / 100;
    onChange(Math.max(MIN_RATE, Math.min(MAX_RATE, next)));
  };

  const atMin = rate <= MIN_RATE;
  const atMax = rate >= MAX_RATE;

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('audioPlayer.playbackSpeed')}
        className={cn(
          'min-w-[44px] min-h-[44px] px-2 flex items-center justify-center rounded-full',
          'font-sans text-sm font-bold tabular-nums',
          'text-on-surface-variant hover:text-tertiary transition-colors',
          'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
        )}
      >
        {formatRate(rate)}
      </button>
      {open && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-label={t('audioPlayer.playbackSpeed')}
          onKeyDown={onKeyDown}
          className={cn(
            'absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10',
            'w-72 max-w-[calc(100vw-2rem)] p-4 rounded-2xl',
            'bg-surface-container border border-outline-variant/20 shadow-2xl',
            'flex flex-col gap-3',
          )}
        >
          <div className="text-center font-serif text-2xl font-bold text-on-surface tabular-nums">
            {formatDisplayRate(rate)}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => adjust(-RATE_STEP)}
              disabled={atMin}
              aria-label={t('audioPlayer.decreaseSpeed')}
              className={cn(speedAdjustButtonClasses())}
            >
              <Minus size={18} weight="bold" />
            </button>
            <input
              type="range"
              min={MIN_RATE}
              max={MAX_RATE}
              step={RATE_STEP}
              value={rate}
              onChange={(e) => onChange(Number(e.target.value))}
              aria-label={t('audioPlayer.playbackSpeed')}
              aria-valuetext={formatRate(rate)}
              className="flex-1 h-1 bg-outline-variant rounded-full cursor-pointer appearance-none accent-tertiary"
            />
            <button
              type="button"
              onClick={() => adjust(RATE_STEP)}
              disabled={atMax}
              aria-label={t('audioPlayer.increaseSpeed')}
              className={cn(speedAdjustButtonClasses())}
            >
              <Plus size={18} weight="bold" />
            </button>
          </div>

          <div
            role="radiogroup"
            aria-label={t('audioPlayer.playbackSpeed')}
            className="grid grid-cols-5 gap-1"
          >
            {PRESET_RATES.map((value) => {
              const selected = value === rate;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => onChange(value)}
                  className={cn(
                    'py-1.5 rounded-full font-sans text-xs font-bold tabular-nums transition-colors',
                    'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
                    selected
                      ? 'bg-tertiary text-on-tertiary'
                      : 'bg-surface-container-highest/60 text-on-surface-variant hover:bg-surface-container-high',
                  )}
                >
                  {formatPresetLabel(value)}
                </button>
              );
            })}
          </div>

          {rate === 1 && (
            <div className="text-xs font-sans text-on-surface-variant">
              {t('audioPlayer.speedNormal')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function speedAdjustButtonClasses() {
  return cn(
    'min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full',
    'bg-surface-container-highest/60 text-on-surface-variant hover:text-tertiary',
    'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
    'disabled:opacity-[0.38] disabled:pointer-events-none',
    'transition-colors',
  );
}

function formatRate(rate: number): string {
  return `${rate}\u00d7`;
}

function formatDisplayRate(rate: number): string {
  return `${rate.toFixed(2)}\u00d7`;
}

function formatPresetLabel(rate: number): string {
  return Number.isInteger(rate) ? rate.toFixed(1) : rate.toString();
}

interface VolumeControlProps {
  volume: number;
  isMuted: boolean;
  onToggleMute: () => void;
  onVolumeChange: (v: number) => void;
}

function VolumeControl({ volume, isMuted, onToggleMute, onVolumeChange }: VolumeControlProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useOutsideInteraction(rootRef, () => setOpen(false));

  const SpeakerIcon = isMuted ? SpeakerSlash : SpeakerHigh;
  const sliderValue = isMuted ? 0 : volume;

  return (
    <div className="relative flex items-center" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        aria-label={t('audioPlayer.volumeLabel')}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          'min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full',
          'text-on-surface-variant hover:text-tertiary transition-colors',
          'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
        )}
      >
        <SpeakerIcon size={22} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('audioPlayer.volumeLabel')}
          className={cn(
            'absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10',
            'p-3 rounded-2xl bg-surface-container border border-outline-variant/20 shadow-2xl',
            'flex flex-col items-center gap-3',
          )}
        >
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={sliderValue}
            onChange={(e) => onVolumeChange(Number(e.target.value))}
            aria-label={t('audioPlayer.volumeLabel')}
            className="h-28 w-1 bg-outline-variant rounded-full cursor-pointer appearance-none accent-tertiary [writing-mode:vertical-lr] [direction:rtl]"
          />
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={isMuted ? t('audioPlayer.unmute') : t('audioPlayer.mute')}
            className="text-on-surface-variant hover:text-tertiary transition-colors focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none rounded-full p-1"
          >
            <SpeakerIcon size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

function useOutsideInteraction(ref: React.RefObject<HTMLElement | null>, onOutside: () => void) {
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onOutside();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [ref, onOutside]);
}
